import React, { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Modal, Space, Table } from 'antd';

import CreateSubgroupModal from '../CreateSubgroupModal';
import UpdateSubgroupModal from '../UpdateSubgroupModal';
import {
  useDeleteSubGroup,
  useSubGroupsQuery,
} from '@/core/domain/operational/SubGroups/SubGroups.hooks';
import { ColumnsType } from 'antd/lib/table';
import { SubGroupByGroup } from '@/core/domain/operational/SubGroups/SubGroups.types';
import { useDisclosure } from '@/hooks';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const SubgroupsModule = () => {
  const intl = useIntl();
  const { groupId } = useRouteMatch<{ groupId: string }>().params;

  const [createSubgroupModalVisible, setCreateSubgroupModalAction] =
    useDisclosure();
  const [updateSubgroupModalVisible, setUpdateSubgroupModalAction] =
    useDisclosure();

  const [selectedSubgroup, setSelectedSubgroup] = useState<SubGroupByGroup>();

  const { data: subGroupData, isLoading } = useSubGroupsQuery(+groupId);

  const removeMutation = useDeleteSubGroup();

  const handleCreateSubgroupModalVisible = () => {
    setCreateSubgroupModalAction.toggle();
  };

  const handleUpdateSubgroupModalVisible = (subgroup?: SubGroupByGroup) => {
    if (updateSubgroupModalVisible) {
      setSelectedSubgroup(undefined);
    } else {
      setSelectedSubgroup(subgroup);
    }

    setUpdateSubgroupModalAction.toggle();
  };

  function handleRemoveSubgroupClick(subgroup: SubGroupByGroup) {
    Modal.confirm({
      title: intl.formatMessage({ id: 'groups.subgroups.remove.modal.title' }),
      content: `${intl.formatMessage({
        id: 'groups.subgroups.remove.modal.content',
      })} "${subgroup.name}"`,
      onOk: async () => {
        try {
          // Fetch Service to remove group item.
          await removeMutation.mutateAsync(subgroup.id);
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'groups.subgroups.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  }

  const subGroupColumns: ColumnsType<SubGroupByGroup> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'subGroups.general.form.description' }),
      ellipsis: true,
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      render: (subgroup) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => handleUpdateSubgroupModalVisible(subgroup),
            }}
            disabled={!hasPermissions([Authorities.REGISTER_SUBGROUP_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveSubgroupClick(subgroup)}
            disabled={!hasPermissions([Authorities.REGISTER_SUBGROUP_DELETE])}
          />
        </Space>
      ),
      width: 100,
      align: 'right',
      fixed: 'right',
    },
  ];

  return (
    <>
      <div className="create-page-title" style={{ marginTop: '32px' }}>
        <SpaceForButton justifyContent="space-between">
          <h2>
            <FormattedMessage id="groups.subgroups" />
          </h2>

          <div className="actions">
            <Button
              type="primary"
              onClick={handleCreateSubgroupModalVisible}
              disabled={!hasPermissions([Authorities.REGISTER_SUBGROUP_ADD])}
            >
              <FormattedMessage id="groups.subgroups.create" />
            </Button>
          </div>
        </SpaceForButton>
      </div>

      <Table
        size="middle"
        rowKey={(row) => row.id}
        loading={isLoading}
        dataSource={subGroupData?.data}
        columns={subGroupColumns}
        pagination={false}
      />

      <CreateSubgroupModal
        visible={createSubgroupModalVisible}
        handleVisible={handleCreateSubgroupModalVisible}
      />

      <UpdateSubgroupModal
        visible={updateSubgroupModalVisible}
        handleVisible={handleUpdateSubgroupModalVisible}
        subgroup={selectedSubgroup}
      />
    </>
  );
};

export default SubgroupsModule;
